<template>
  <v-container data-scroll-focus="eventProductsContainer" class="mt-0 pt-0">
    <v-row>
      <v-col cols="12" md="6">
        <slot name="label">
          <h3 style="font-size: 16px">
            {{
              $t(
                "events:EventPage.Schedule.OtherServicesAndOffers.heading",
                "Other services and offers",
                "Schedule section on event page: subheading for other services and offers"
              )
            }}
          </h3>
        </slot>
      </v-col>
      <v-col
        v-if="canAdd"
        class="text-md-right text-center"
        cols="12"
        md="6"
        :class="$vuetify.display.mdAndUp ? '' : 'py-5'"
      >
        <template v-if="$vuetify.display.mdAndUp">
          <v-btn color="edit" data-test="addProduct" :to="addNewItem()">
            <v-icon color="white" size="small" start> $iconPlus </v-icon>
            {{ $t("common:button.addProduct", "Add product", "Add button") }}
          </v-btn>
        </template>
        <template v-else>
          <v-btn
            color="edit"
            min-width="295px!important"
            max-width="295px"
            min-height="40px!important"
            width="100%"
            data-test="addProduct"
            :to="addNewItem()"
          >
            <v-icon color="white" size="x-small" start> $iconPlus </v-icon>
            {{ $t("common:button.addProduct", "Add product", "Add button") }}
          </v-btn>
        </template>
      </v-col>
    </v-row>
    <div>
      <template v-if="$vuetify.display.mdAndUp">
        <EventProductsDesktop
          v-bind="{
            visibleProductCategories,
            state,
            actions
          }"
        />
      </template>
      <template v-else>
        <EventProductsMobile
          v-bind="{
            visibleProductCategories,
            state,
            actions
          }"
        />
      </template>
    </div>
    <p
      class="text-muted v-col-12 text-center text-md-left"
      :style="$vuetify.display.smAndDown ? 'font-size: 12px;' : ''"
    >
      {{
        $t(
          "events:EventPage.Schedule.OtherServicesAndOffers.disclaimer",
          "Other services and offers can have different payment conditions, the whole or some part of fees may be non-refundable in case of cancellations. Check payment conditions before booking.",
          "Schedule section on event page, disclaimer for other services and offers"
        )
      }}
    </p>
  </v-container>
</template>
<script>
import gql from "fraql";
import { useCurrency } from "@composables/masterdata/config";
import { sortBy } from "lodash-es";
import EventProductsDesktop from "./EventProductsDesktop.vue";
import EventProductsMobile from "./EventProductsMobile.vue";
import { useEventProductCategoryHelper } from "@composables/masterdata/products/eventProductCategories";
import ShowPaymentStructure from "@elements/ShowPaymentStructure.vue";
import eventProductLockStatus from "@/utils/eventProductLockStatus.js";
import { showLoadingButton } from "@/utils/showLoadingButton";

export default {
  name: "EventProducts",
  components: {
    EventProductsDesktop,
    EventProductsMobile
  },
  inject: {
    isNoncompetition: {
      default: false
    }
  },
  props: {
    event: {
      type: Object,
      required: true
    }
  },
  setup() {
    const { masterEventProductCategories } = useEventProductCategoryHelper();
    return {
      ...useCurrency(),
      masterEventProductCategories
    };
  },
  data() {
    return {
      expanded: []
    };
  },
  computed: {
    eventProducts() {
      return this.event.eventProducts;
    },
    visibleProductCategories() {
      const sorted = sortBy(this.masterEventProductCategories, "rank");
      return sorted.filter(product => {
        return this.eventProducts.some(
          eventProduct => eventProduct.productId == product.id
        );
      });
    },
    canAdd() {
      return this.$allowedMutation(
        this.event.allowedMutations,
        "eventEventProductCreateOrUpdate"
      );
    },
    state() {
      return Object.defineProperties(
        {},
        {
          useOnlinePayments: {
            get: () => {
              return this.event.useOnlinePayments;
            }
          },
          canAdd: {
            get: () => {
              return this.canAdd;
            }
          },
          expanded: {
            get: () => {
              return this.expanded;
            }
          }
        }
      );
    },
    actions() {
      return {
        filteredEventProducts: this.filteredEventProducts,
        addNewItem: this.addNewItem,
        addItem: this.addItem,
        editItem: this.editItem,
        cloneItem: this.cloneEventProduct,
        deleteItem: this.deleteItem,
        expand: this.expand,
        masterCurrency: this.masterCurrency,
        eventProductLocked: eventProductLockStatus
      };
    }
  },
  methods: {
    expand(item) {
      if (this.expanded.includes(item.id)) {
        this.expanded = this.expanded.filter(x => x != item.id);
      } else {
        this.expanded.push(item.id);
      }
    },
    filteredEventProducts(productId) {
      return sortBy(
        this.eventProducts.filter(p => p.productId == productId),
        "name",
        "asc"
      );
    },
    async cloneEventProduct(eventProduct, node) {
      const response = await this.$root.$confirm(
        this.$t("events:competion.cloneEventProduct.heading"),
        this.$t("events:competion.cloneEventProduct.text"),
        {
          confirmText: this.$t("common:button.confirm")
        }
      );
      if (!response) return;
      const removeLoading = showLoadingButton(node.target);
      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation eventProductClone($id: ID!) {
              eventProductClone(input: { id: $id }) {
                eventProduct {
                  id
                }
              }
            }
          `,
          variables: {
            id: eventProduct.id
          }
        });

        this.$root.$snackbar("success", {
          redirection: true
        });
        this.$router.push({
          name: "EventProductFormEdit",
          params: {
            eventProductId: data.eventProductClone.eventProduct.id,
            eventId: this.event.id
          },
          state: {
            returnRoute: {
              name: this.$route.name,
              params: {
                eventId: this.event.id
              },
              state: {
                routeScrollState: {
                  querySelector: `[data-scroll-focus="eventProduct-${data.eventProductClone.eventProduct.id}"]`
                }
              }
            }
          }
        });
      } catch (error) {
        this.$root.$snackbarServerError(error, { level: "error" });
      } finally {
        removeLoading();
      }
    },
    async deleteItem(id, node) {
      const response = await this.$root.$confirm(
        this.$t("common:confirmation.delete", "Delete product"),
        this.$t("common:confirmation.areYouSure", "Are you sure?"),
        { color: "red" }
      );
      if (!response) return;
      const removeLoading = showLoadingButton(node.target);
      try {
        await this.$apollo.mutate({
          mutation: gql`
            mutation eventProductDelete($id: ID!) {
              eventProductDelete(input: { id: $id }) {
                event {
                  id
                  eventProducts {
                    id
                    eventId
                    productId
                    name
                    priceCents
                    bookingFeeCents
                    description
                    paymentStructure
                    confirmedPurchasesCount
                  }
                }
              }
            }
          `,
          variables: { id }
        });

        this.$root.$snackbar("success", {
          text: this.$t(
            "common:feedbackMessage.removedSuccessfully.text",
            "Succesfully deleted!"
          )
        });
      } catch (error) {
        this.$root.$snackbarServerError(error, { level: "error" });
      } finally {
        removeLoading();
      }
    },
    addNewItem() {
      const name = this.isNoncompetition
        ? "NoncompetitionEventProductForm"
        : "EventProductForm";
      return {
        name: name,
        params: {
          eventId: this.event.id
        },
        state: {
          returnRoute: {
            name: this.$route.name,
            params: {
              eventId: this.event.id
            },
            state: {
              routeScrollState: {
                querySelector: "[data-scroll-focus='eventProductsContainer']"
              }
            }
          }
        }
      };
    },
    addItem(id) {
      const name = this.isNoncompetition
        ? "NoncompetitionEventProductForm"
        : "EventProductForm";
      return {
        name: name,
        params: {
          eventId: this.event.id
        },
        query: { productId: id },
        state: {
          returnRoute: {
            name: this.$route.name,
            params: {
              eventId: this.event.id
            },
            state: {
              routeScrollState: {
                querySelector: `[data-scroll-focus='product-${id}']`
              }
            }
          }
        }
      };
    },
    editItem(item) {
      const name = this.isNoncompetition
        ? "NoncompetitionEventProductFormEdit"
        : "EventProductFormEdit";
      return {
        name: name,
        params: {
          eventProductId: item.id,
          eventId: this.event.id
        },
        state: {
          returnRoute: {
            name: this.$route.name,
            params: {
              eventId: this.event.id
            },
            state: {
              routeScrollState: {
                querySelector: `[data-scroll-focus='eventProduct-${item.id}']`
              }
            }
          }
        }
      };
    }
  },
  fragments: {
    eventProducts: gql`
      fragment _ on Event {
        eventProducts {
          id
          eventId
          productId
          registrationStatus
          name
          refCode
          priceCents
          bookingFeeCents
          description
          paymentStructure
          vatRateId
          ${ShowPaymentStructure.fragments.eventProduct}
          confirmedPurchasesCount
          limitPurchasesForPublicRegistration
          product {
            id
            name
          }
          eventProductCustomFields {
            id
          }
          allowedMutations
        }
      }
    `
  }
};
</script>
